import { useCallback, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { Patient } from '@medplum/fhirtypes'
import { linkZocdocPatient, replaceZocdocPatientId } from './patientSync'

/**
 * Binding a beHuman chart to a Zocdoc patient, and saying in words what happened when it could not
 * be bound.
 *
 * Extracted because three surfaces now perform the identical act — the details popup's candidate
 * table, the row's candidate popup, and the create-and-invite flow that links the chart it just
 * made. The refusals are the reason this is shared rather than copied: `conflict` and `holds-other`
 * each need a sentence that names the ids involved and says what was *not* changed, and three
 * hand-written versions of that would drift apart exactly where an operator is deciding whether two
 * records are the same human being.
 */

/** Written when a link is refused, so the sentence is composed in one place. */
function refusalMessage(
  result: Awaited<ReturnType<typeof linkZocdocPatient>>,
  zocdocPatientId: string,
): string | null {
  if (result.status === 'conflict') {
    return `Zocdoc patient ${zocdocPatientId} is already linked to beHuman chart ${result.existing}. Moving it here would re-point one patient's history at another person, so nothing was changed.`
  }
  if (result.status === 'holds-other') {
    // Not appended, by design: one chart carries one Zocdoc patient. Replacing is offered beside
    // this message rather than done on the operator's behalf.
    return `This chart is already linked to Zocdoc patient ${result.existing.join(', ')}. A chart carries one Zocdoc patient, so nothing was added — use “Replace the link” if ${zocdocPatientId} is the correct one.`
  }
  return null
}

export type ZocdocPatientLink = {
  /** Which chart is mid-write, so only its own button spins. `null` when idle. */
  linking: string | null
  /** Why the last attempt changed nothing, in a sentence an operator can act on. */
  linkError: string | null
  setLinkError: (message: string | null) => void
  /**
   * Writes this appointment's Zocdoc patient id onto `patientId`.
   *
   * `replace` is the operator saying "the id on this chart is the wrong one" — a separate decision
   * from "this is the same person", and the only way a recorded binding is discarded.
   *
   * Resolves `true` when the chart now carries the id, so a caller can close a popup on success
   * and leave it open with the reason on screen otherwise.
   */
  confirmSamePatient: (patientId: string, replace?: boolean) => Promise<boolean>
  /**
   * Links a chart that was just created, reporting failure as text rather than as a rejection.
   *
   * Separate from `confirmSamePatient` because the caller is past the point of no return: the chart
   * exists and the invitation has been sent, so "the link did not happen" must be reported *beside*
   * that success and never as a failure of it. An operator told only "could not link" would
   * reasonably conclude the chart was not created and make a second one.
   */
  linkCreatedChart: (created: Patient) => Promise<{ linked: boolean; detail?: string }>
}

/**
 * @param zocdocPatientId this appointment's Zocdoc patient id, or `undefined` when the participants
 * read gave none — every action then refuses up front rather than writing a blank identifier.
 * @param onLinked called after a successful write; the list is re-read rather than patched locally,
 * because `associatedPatients` is computed server-side and a hand-maintained copy of it would be
 * one more thing that can disagree with the next load.
 */
export function useZocdocPatientLink(
  zocdocPatientId: string | undefined,
  onLinked?: () => void,
): ZocdocPatientLink {
  const medplum = useMedplum()
  const [linking, setLinking] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)

  const confirmSamePatient = useCallback(
    async (patientId: string, replace = false) => {
      if (!zocdocPatientId) return false
      setLinking(patientId)
      setLinkError(null)
      try {
        const result = replace
          ? await replaceZocdocPatientId(medplum, patientId, zocdocPatientId)
          : await linkZocdocPatient(medplum, patientId, zocdocPatientId)
        const refusal = refusalMessage(result, zocdocPatientId)
        if (refusal) {
          setLinkError(refusal)
          return false
        }
        onLinked?.()
        return true
      } catch (cause) {
        setLinkError(cause instanceof Error ? cause.message : 'Could not update the chart')
        return false
      } finally {
        setLinking(null)
      }
    },
    [medplum, onLinked, zocdocPatientId],
  )

  const linkCreatedChart = useCallback(
    async (created: Patient) => {
      if (!created.id) {
        return { linked: false, detail: 'the new chart came back without an id' }
      }
      if (!zocdocPatientId) {
        // Not a failure: Zocdoc simply told us no patient id, so there is nothing to write.
        return { linked: false, detail: 'Zocdoc gave no patient id for this booking' }
      }
      try {
        const result = await linkZocdocPatient(medplum, created.id, zocdocPatientId)
        const refusal = refusalMessage(result, zocdocPatientId)
        return refusal ? { linked: false, detail: refusal } : { linked: true }
      } catch (cause) {
        return {
          linked: false,
          detail: cause instanceof Error ? cause.message : 'the identifier could not be written',
        }
      }
    },
    [medplum, zocdocPatientId],
  )

  return { linking, linkError, setLinkError, confirmSamePatient, linkCreatedChart }
}
